import { authenticatedAppViewRequest, type AuthenticatedState, authState } from "../auth.ts";
import { AuthorizePrompt, SignInForm } from "../components/auth-panel.tsx";
import { Feed } from "../components/feed.tsx";
import { PageHeader } from "../components/page-header.tsx";
import { RpcClient } from "../api.ts";
import { getFollowingFeed } from "../../queries.ts";
import { config } from "../config.ts";
import { canCallRpc } from "../permissions.ts";

export function HomeView(signal: AbortSignal): Element {
  let view = new AbortController();
  const home = <main class="home"></main>;
  const subscription = authState.subscribeImmediate((state) => {
    view.abort();
    view = new AbortController();
    if (state.status === "loading") {
      home.replaceChildren(
        <section class="home-loading">
          <p class="status">Checking your account…</p>
        </section>,
      );
    } else if (state.status === "authenticated") {
      home.replaceChildren(authenticatedHome(state, view.signal));
    } else {
      home.replaceChildren(signedOutHome(state.message, view.signal));
    }
  });
  signal.addEventListener("abort", () => {
    subscription.unsubscribe();
    view.abort();
  }, { once: true });
  return home;
}

function signedOutHome(message: string | undefined, signal: AbortSignal): Element {
  return (
    <section class="home-sign-in">
      <div class="welcome-card">
        <h1>Deluxe, independent microblogging on atproto</h1>
        <ul class="feature-list">
          <li>multi-channel accounts</li>
          <li>post edits & markup</li>
          <li>65,536 post length limit</li>
          <li>8 MiB per-image size limit</li>
          <li>small-net discussion &lt;3</li>
        </ul>
        <div class="home-sign-in-box">
          <h2>Sign in with your AT Protocol account</h2>
          <p>Enter your handle or DID to continue through your PDS.</p>
          {SignInForm(message, signal)}
        </div>
      </div>
    </section>
  );
}

function authenticatedHome(
  state: AuthenticatedState,
  signal: AbortSignal,
): Element {
  const body = <div></div>;
  const refresh = (
    <button class="icon-button" type="button" aria-label="Refresh" title="Refresh">
      <span class="lucide lucide-refresh" aria-hidden="true"></span>
    </button>
  ) as HTMLButtonElement;
  const section = (
    <section class="feed-section">
      {PageHeader({ title: "Following", action: refresh })}
      {body}
    </section>
  );
  if (!canCallRpc(state.scopes, getFollowingFeed.id, config.appViewService)) {
    refresh.hidden = true;
    body.append(...AuthorizePrompt(
      state.did,
      "Authorize timeline access to see posts from people you follow.",
      "Authorize timeline",
      "/",
      signal,
    ));
    return section;
  }
  const client = new RpcClient(authenticatedAppViewRequest);
  let page = new AbortController();
  const load = () => {
    page.abort();
    page = new AbortController();
    const requestSignal = AbortSignal.any([signal, page.signal]);
    body.replaceChildren(Feed((cursor) =>
      client.call(getFollowingFeed, {
        parameters: { limit: 30, ...(cursor && { cursor }) },
        signal: requestSignal,
      }), requestSignal));
  };
  refresh.addEventListener("click", load);
  load();
  return section;
}
